// =============================================================================
// features/sales/campaigns/detail/tabs/ReturnsTab.tsx — Aba "Retornos":
// compromissos vinculados à campanha (doc 26 §6.7). Nota do slot: "consome a
// API de agenda (F33-S02) filtrada por campanha; não criar endpoint novo" —
// mas `AppointmentListQuerySchema` só filtra por `campaignMemberId` (array),
// nunca por `campaignId` diretamente (nenhum endpoint publica isso ainda,
// ver packages/shared-schemas/src/sales-appointments.ts).
//
// Composição em DUAS chamadas reais, ambas já paginadas pelo servidor —
// nunca "carregar tudo para filtrar no cliente":
//   1. `GET /members?status=waiting_callback` — a fila de campaign_members
//      esperando retorno é o universo de "possui retorno vinculado a esta
//      campanha" (o worker de fila só cria um contact_appointments quando o
//      outcome exige agendamento — SALES_OUTCOME_META.requiresAppointment —
//      e sempre move o membro para `waiting_callback`, ver
//      queue/service.ts). Paginado normalmente (PAGE_SIZE), igual à Carteira.
//   2. `GET /appointments?campaignMemberId=<ids da página>` — busca os
//      compromissos reais (data/hora, canal, observação) SÓ dos ids já
//      paginados na chamada 1. Nunca um array de milhares de ids.
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import * as React from 'react';

import type { ResponsiveTableColumn } from '../../../../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../../../../components/ui/ResponsiveTable';
import type { Appointment } from '../../../../../hooks/sales/useAppointments';
import { useAppointments } from '../../../../../hooks/sales/useAppointments';
import { useCampaignMembers } from '../../../../../hooks/sales/useCampaignDetail';
import { formatOrgDateTime } from '../../../../../lib/orgDateTime';
import { DetailPagination } from '../components/DetailPagination';

export interface ReturnsTabProps {
  campaign: Campaign;
}

const PAGE_SIZE = 25;

const CHANNEL_LABEL: Record<Appointment['channel'], string> = {
  phone_manual: 'Ligação manual',
  phone_integrated: 'Ligação integrada',
  whatsapp: 'WhatsApp',
  in_person: 'Presencial',
  other: 'Outro',
};

export function ReturnsTab({ campaign }: ReturnsTabProps): React.JSX.Element {
  const [offset, setOffset] = React.useState(0);

  const membersFilters = React.useMemo(
    () => ({
      status: ['waiting_callback' as const],
      sortBy: 'nextActionAt' as const,
      sortOrder: 'asc' as const,
      limit: PAGE_SIZE,
      offset,
    }),
    [offset],
  );

  const membersQuery = useCampaignMembers(campaign.id, membersFilters);
  const memberIds = React.useMemo(
    () => (membersQuery.data?.data ?? []).map((m) => m.id),
    [membersQuery.data],
  );

  const appointmentsQuery = useAppointments(
    { campaignMemberId: memberIds, sortBy: 'scheduledAt', sortOrder: 'asc', limit: PAGE_SIZE },
    { enabled: memberIds.length > 0 },
  );

  const isLoading = membersQuery.isLoading || (memberIds.length > 0 && appointmentsQuery.isLoading);
  const isError = membersQuery.isError || appointmentsQuery.isError;
  const appointments = memberIds.length > 0 ? (appointmentsQuery.data?.data ?? []) : [];

  const columns: ResponsiveTableColumn<Appointment>[] = [
    {
      key: 'lead',
      header: 'Cliente',
      primary: true,
      cell: (a) => <span className="font-mono text-sm text-ink">Lead {a.leadId.slice(0, 8)}…</span>,
    },
    {
      key: 'scheduledAt',
      header: 'Retorno agendado',
      cell: (a) => (
        <span
          className={`font-sans text-sm ${a.isOverdue ? 'text-danger font-semibold' : 'text-ink-2'}`}
        >
          {formatOrgDateTime(a.scheduledAt)}
          {a.isOverdue && ' — atrasado'}
        </span>
      ),
    },
    {
      key: 'channel',
      header: 'Canal',
      hideBelow: 'md',
      cell: (a) => <span className="font-sans text-sm text-ink-2">{CHANNEL_LABEL[a.channel]}</span>,
    },
    {
      key: 'reason',
      header: 'Motivo',
      hideBelow: 'lg',
      cell: (a) => <span className="font-sans text-sm text-ink-3">{a.reason ?? '—'}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-md border border-border overflow-hidden"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-1)' }}
      >
        {isError ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center px-4">
            <p className="font-sans text-sm text-ink-3">Não foi possível carregar os retornos.</p>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={appointments}
              getRowKey={(a) => a.id}
              isLoading={isLoading}
              aria-label="Retornos vinculados à campanha"
              emptyState={
                <div className="flex flex-col items-center gap-2 py-12 text-center px-4">
                  <p className="font-sans text-sm text-ink-3">
                    Nenhum retorno pendente vinculado a esta campanha.
                  </p>
                </div>
              }
            />
            <DetailPagination
              offset={offset}
              limit={PAGE_SIZE}
              total={membersQuery.data?.total ?? 0}
              onOffsetChange={setOffset}
              itemLabel="retornos"
            />
          </>
        )}
      </div>
    </div>
  );
}
